import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import styles from './SpecialModeView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import { celebrate, popScale, shake } from './juice'
import { cueCorrect, cueWrong, cueComplete, isMuted } from './cues'
import ShareWithTeacher from './ShareWithTeacher'

import CloseIcon from '@mui/icons-material/Close'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import TableChartIcon from '@mui/icons-material/TableChart'

// English Special Mode — the two places speakers of article-less languages with
// a different tense system (Turkish and Russian among them) reliably trip:
//
//   Tenses   — pick the tense the sentence needs, then write the verb phrase
//   Articles — a, an, the, or no article at all
//
// Both write their sets with the Mac Studio model, then run a second, strict
// checking pass that corrects each answer and DROPS any sentence where two
// answers would sound natural. A drill that marks a defensible answer wrong
// teaches the wrong lesson, so a slightly shorter set is the better trade.
// Both end on a summary the learner can send to their teacher.

const MODEL = 'gemma4:26b'
const clip = (s, n) => String(s == null ? '' : s).trim().slice(0, n)
const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)
const tidySentence = (s) => String(s || '').replace(/\s+/g, ' ').replace(/\s+([.,!?;:])/g, '$1').trim()
const focusLine = (query, fallback) => (String(query || '').trim() ? `Focus on: "${String(query).trim()}".` : fallback)

const parseItems = (raw) => {
  if (!raw) return []
  let t = String(raw).replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = t.indexOf('{'), b = t.lastIndexOf('}')
  if (a !== -1 && b !== -1) t = t.slice(a, b + 1)
  try { const o = JSON.parse(t); return Array.isArray(o?.items) ? o.items : [] } catch { return [] }
}

async function askModel(serverUrl, apiKey, prompt, temperature, numPredict) {
  const res = await fetch(`${serverUrl}/api/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
    body: JSON.stringify({ model: MODEL, prompt, stream: false, keep_alive: '30m', format: 'json', options: { temperature, num_predict: numPredict } }),
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return parseItems((await res.json()).response)
}

const failureMessage = (e, fallback) => ((e?.message || '').match(/Failed to fetch|Load failed/)
  ? 'Connection to the Mac Studio failed — check the tunnel / Ollama.'
  : (e?.message || fallback))

const markOf = (r) => (!r || r.skipped ? '↷' : r.flagged ? '⚑' : r.revealed ? '👁' : '↻')

// ── answers compared on words ────────────────────────────────────────────────
// Case, curly quotes, punctuation and n't-contractions don't matter: "hasn't
// lived" is "has not lived". "'s" is left alone because it could be "is" or
// "has" — the model lists those spellings itself, per item.
const CONTRACTIONS = [
  [/\bwon't\b/g, 'will not'], [/\bcan't\b/g, 'cannot'], [/\bshan't\b/g, 'shall not'], [/n't\b/g, ' not'],
  [/'ll\b/g, ' will'], [/'ve\b/g, ' have'], [/'re\b/g, ' are'], [/'m\b/g, ' am'],
  [/\bcan not\b/g, 'cannot'],
]
export const normEn = (s) => {
  let t = String(s || '').toLowerCase().replace(/[’‘`´]/g, "'")
  for (const [re, rep] of CONTRACTIONS) t = t.replace(re, rep)
  return t.replace(/[^a-z' ]+/g, ' ').replace(/\s+/g, ' ').trim()
}
// "she has lived" typed into "She ___ in Lisbon" is still right: drop any words
// the learner repeated from just before the gap. Never drops the whole answer.
export const stripLeadIn = (typedNorm, prefixNorm) => {
  const t = String(typedNorm || '').split(' ').filter(Boolean)
  const p = String(prefixNorm || '').split(' ').filter(Boolean)
  for (let n = Math.min(p.length, t.length - 1); n > 0; n--) {
    if (p.slice(-n).join(' ') === t.slice(0, n).join(' ')) return t.slice(n).join(' ')
  }
  return t.join(' ')
}

// ── shared pieces ────────────────────────────────────────────────────────────
function SetLoading({ label, phase, loadingMsg, errorMsg, themeClass, themeBtn, onBack, onRetry }) {
  return (
    <div className={`${styles.view} ${themeClass}`}>
      {themeBtn}
      <button className={styles.closeBtn} onClick={onBack}><CloseIcon /></button>
      <div className={styles.panel}>
        <div className={styles.modeLabel}>{label}</div>
        {phase === 'loading'
          ? <div className={styles.loading}><div className={styles.spinner} /><div className={styles.loadingText}>{loadingMsg}</div></div>
          : (
            <>
              <div className={styles.error}>{errorMsg}</div>
              <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.6rem' }}>
                <button className={styles.linkBtn} onClick={onBack}>Back</button>
                <button className={styles.generateBtn} onClick={onRetry}>Try again</button>
              </div>
            </>
          )}
      </div>
    </div>
  )
}

// A reference card of rules. `highlight` is only set once the learner already
// knows the answer, so opening it mid-question never gives the answer away.
function RuleBoard({ title, rows, highlight, onClose }) {
  return (
    <div className={styles.refOverlay} onClick={onClose}>
      <div className={styles.refModal} onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.modeLabel}>{title}</div>
        <div className={styles.sumList} style={{ marginTop: '0.6rem' }}>
          {rows.map(r => (
            <div key={r.key} className={styles.sumRow}
              style={r.key === highlight ? { outline: '1px solid var(--accent, #6ee7b7)', borderRadius: 10 } : undefined}>
              <span className={styles.sumSent}>
                <span><b className={styles.sumAns}>{r.name}</b>{r.form ? ` · ${r.form}` : ''}</span>
                <span className={styles.sumWhy}>{r.use}{r.ex ? ` — ${r.ex}` : ''}</span>
              </span>
            </div>
          ))}
        </div>
        <div className={styles.refFoot}><button className={styles.checkBtn} onClick={onClose}>Got it</button></div>
      </div>
    </div>
  )
}

// The end of a set: a grade, the numbers, exactly what to redo, and — the
// learner's choice — sending it to the teacher who teaches them English. Not
// dismissed by a stray click on the backdrop: that would lose the share.
function EndOfSet({ title, rows, stats, mode, buildDetails, onRetry, onNext, onDone }) {
  const total = rows.length
  const perfect = rows.filter(r => r.perfect).length
  const pct = Math.round((perfect / Math.max(1, total)) * 100)
  const grade = pct === 100 ? '🎉 Every one first time' : pct >= 80 ? 'Strong work' : pct >= 50 ? 'Getting there' : 'Worth another pass'
  const tricky = rows.filter(r => !r.perfect)
  return (
    <div className={styles.refOverlay}>
      <div className={styles.refModal} style={{ maxWidth: 640 }}>
        <button className={styles.closeBtn} onClick={onDone} title="Back to the settings"><CloseIcon /></button>
        <div className={styles.modeLabel}>{title}</div>
        <h2 className={styles.sumGrade}>{grade}</h2>
        <div className={styles.sumStats}>
          <div className={styles.sumStat}><span className={styles.sumNum}>{perfect}/{total}</span><span className={styles.sumLabel}>first time</span></div>
          <div className={styles.sumStat}><span className={styles.sumNum}>{pct}%</span><span className={styles.sumLabel}>clean</span></div>
          {stats.map(([n, label]) => (
            <div key={label} className={styles.sumStat}><span className={styles.sumNum}>{n}</span><span className={styles.sumLabel}>{label}</span></div>
          ))}
        </div>

        {tricky.length > 0 ? (
          <>
            <div className={styles.sumHead}>Worth another look</div>
            <div className={styles.sumList}>
              {tricky.map((r, i) => (
                <div key={i} className={styles.sumRow}>
                  <span className={styles.sumMark}>{r.mark}</span>
                  <span className={styles.sumSent}>
                    <span>{r.before} <b className={styles.sumAns}>{r.answer}</b> {r.after}</span>
                    <span className={styles.sumWhy}>{r.note}</span>
                  </span>
                </div>
              ))}
            </div>
          </>
        ) : <div className={styles.sumClean}>Nothing to review — clean sweep.</div>}

        <ShareWithTeacher title={title} langCode="eng" mode={mode} total={total} perfect={perfect} buildDetails={buildDetails} />

        <div className={styles.refFoot}>
          {tricky.length > 0 && <button className={styles.checkBtn} onClick={onRetry}>Redo the {tricky.length} tricky {tricky.length === 1 ? 'one' : 'ones'} →</button>}
          <button className={styles.linkBtn} onClick={onNext}>new set</button>
          <button className={styles.linkBtn} onClick={onDone}>done</button>
        </div>
      </div>
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// Tenses
// ═════════════════════════════════════════════════════════════════════════════
export const EN_TENSE_CHOICES = [
  { key: 'pres-simple', label: 'Present simple', short: 'pres. simple', form: 'she works', use: 'Habits, routines, facts and timetables.', cues: 'usually, every day, often, never' },
  { key: 'pres-cont', label: 'Present continuous', short: 'pres. cont.', form: 'she is working', use: 'Happening right now, or only for a while.', cues: 'now, at the moment, this week, Look!' },
  { key: 'past-simple', label: 'Past simple', short: 'past simple', form: 'she worked · went', use: 'Finished, at a finished time.', cues: 'yesterday, last year, in 2019, two days ago' },
  { key: 'pres-perfect', label: 'Present perfect', short: 'pres. perfect', form: 'she has worked', use: 'The past connected to now — an experience, a result, or a time that is not over.', cues: 'ever, never, just, already, yet, since, for' },
  { key: 'past-cont', label: 'Past continuous', short: 'past cont.', form: 'she was working', use: 'In progress at a moment in the past, often interrupted.', cues: 'while, when…, at 8 pm yesterday' },
  { key: 'will', label: 'Future: will', short: 'will', form: 'she will work', use: 'Deciding now, predicting, promising, offering.', cues: 'I think, probably, OK — I’ll…' },
  { key: 'going-to', label: 'Future: going to', short: 'going to', form: 'she is going to work', use: 'A plan already made, or a prediction from what you can see.', cues: 'next week (already planned), Look at those clouds!' },
]
// The classic confusions first: simple against continuous, past simple
// against present perfect.
export const EN_TENSE_DEFAULT = ['pres-simple', 'pres-cont', 'past-simple', 'pres-perfect']
const TENSE_BY_KEY = Object.fromEntries(EN_TENSE_CHOICES.map(t => [t.key, t]))

const tensePrompt = ({ count, query, choices }) => `You are an experienced English teacher writing a TENSE-CHOICE exercise for an intermediate learner whose first language is not English. Keep the vocabulary simple and the sentences natural and everyday.
${focusLine(query, 'Use everyday topics and a mix of regular and common irregular verbs.')}

Create ${count} items. Each is ONE sentence with ONE verb gap, written ⟦V⟧. The sentence must contain a clear signal — a time expression or context — so that EXACTLY ONE of these tenses is correct:
${choices.map(c => `- "${c.key}" = ${c.label}: ${c.use} Typical signals: ${c.cues}.`).join('\n')}
Spread the items roughly evenly across those tenses, and vary the subject (I, you, he, she, we, they, a name).

For each item:
- verb: the base form of the verb (e.g. "live")
- tense: one of ${choices.map(c => `"${c.key}"`).join(', ')}
- answer: exactly the words that fill ⟦V⟧ — the whole verb phrase INCLUDING any helper verb ("has lived", "is working", "went"), but NEVER the subject
- alternatives: other correct spellings of the same answer, such as contractions ("'s lived"); [] if none
- sentenceTemplate: the complete sentence with the gap written as ⟦V⟧ and every other word already correct (e.g. "She ⟦V⟧ in Lisbon since 2019.")
- cue: the words in the sentence that signal the tense (e.g. "since 2019")
- why: ONE short, simple English sentence explaining why that tense is needed

Rules: exactly one ⟦V⟧ per sentence; statements unless the focus asks for questions or negatives; never write a sentence where two of the listed tenses would both sound natural.
Return ONLY minified JSON, no markdown:
{"items":[{"verb":"","tense":"","answer":"","alternatives":[],"sentenceTemplate":"","cue":"","why":""}]}`

const verifyTensePrompt = (drafts, choices) => `You are a strict English grammar checker. Each numbered sentence has one verb gap ⟦V⟧ and the base verb to put in it.
For each, decide:
- "tense": which ONE of these tenses the sentence requires, given its signal words: ${choices.map(c => `"${c.key}" (${c.label})`).join(', ')}. If it truly needs a tense that is not in this list, write "other".
- "answer": exactly the words that fill ⟦V⟧ in that tense — include any helper verb, never the subject.
- "ambiguous": true if two of the listed tenses would both sound natural to a native speaker; otherwise false.
- "why": ONE short, simple sentence explaining the choice.
${drafts.map((d, i) => `${i + 1}. "${d.prefix} ⟦V⟧ ${d.suffix}" — verb: ${d.verb} — draft: ${d.tense} / ${d.answer}`).join('\n')}
Return ONLY JSON {"items":[{"tense":"","answer":"","ambiguous":false,"why":""}]} with EXACTLY ${drafts.length} items in the SAME order.`

export const shapeTenseItem = (raw, allowed) => {
  const tense = String(raw?.tense || '').trim()
  const verb = String(raw?.verb || '').trim()
  const answer = String(raw?.answer || '').trim().replace(/\s+/g, ' ')
  const parts = String(raw?.sentenceTemplate || '').trim().split(/⟦[^⟧]*⟧/)
  if (!allowed.includes(tense) || !verb || !answer || parts.length !== 2) return null
  const prefix = parts[0].trim()
  const suffix = parts[1].trim()
  // The model sometimes repeats the subject inside the answer. The sentence
  // already has it, so strip it rather than throw away a good item.
  const words = answer.split(' ')
  const lead = normEn(prefix).split(' ').filter(Boolean)
  let drop = 0
  for (let n = Math.min(lead.length, words.length - 1); n > 0; n--) {
    if (lead.slice(-n).join(' ') === words.slice(0, n).map(normEn).join(' ')) { drop = n; break }
  }
  const finalAnswer = words.slice(drop).join(' ')
  const alternatives = (Array.isArray(raw?.alternatives) ? raw.alternatives : []).map(s => String(s || '').trim()).filter(Boolean)
  return {
    verb, tense, answer: finalAnswer, alternatives, prefix, suffix,
    sentence: tidySentence(`${prefix} ${finalAnswer} ${suffix}`),
    cue: String(raw?.cue || '').trim(),
    why: String(raw?.why || '').trim(),
  }
}

export const applyTenseVerdicts = (drafts, verdicts, allowed) => drafts.map((d, i) => {
  const v = verdicts[i] || {}
  if (v.ambiguous === true || v.ambiguous === 'true') return null
  const tense = String(v.tense || '').trim()
  if (!allowed.includes(tense)) return null          // needs a tense the learner isn't practising
  const answer = String(v.answer || '').trim()
  const changed = answer && normEn(answer) !== normEn(d.answer)
  return shapeTenseItem({
    verb: d.verb, tense, answer: answer || d.answer,
    alternatives: changed ? [] : d.alternatives,      // spellings of the old answer no longer apply
    sentenceTemplate: `${d.prefix} ⟦V⟧ ${d.suffix}`, cue: d.cue,
    why: String(v.why || '').trim() || d.why,
  }, allowed)
}).filter(Boolean)

export function EnglishTenseView({ tenseKeys, count = 10, query = '', voiceSpeed, themeClass = '', themeBtn = null, onBack }) {
  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const apiKey = process.env.REACT_APP_OLLAMA_KEY
  // At least two tenses, or there is no choice to make.
  const allowed = useMemo(() => {
    const keys = (tenseKeys || []).filter(k => TENSE_BY_KEY[k])
    return keys.length >= 2 ? keys : EN_TENSE_DEFAULT
  }, [tenseKeys])
  const choices = useMemo(() => allowed.map(k => TENSE_BY_KEY[k]), [allowed])

  const [phase, setPhase] = useState('loading')      // loading | drill | error
  const [loadingMsg, setLoadingMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [items, setItems] = useState([])
  const [idx, setIdx] = useState(0)
  const [setNum, setSetNum] = useState(1)
  const [regen, setRegen] = useState(false)
  const [results, setResults] = useState({})         // idx → how that item went
  const [showSummary, setShowSummary] = useState(false)

  const [stage, setStage] = useState('pick')         // pick | produce | done
  const [choice, setChoice] = useState(null)
  const [pickResult, setPickResult] = useState(null) // right | wrong | revealed
  const [pickAttempts, setPickAttempts] = useState(0)
  const [showHint, setShowHint] = useState(false)
  const [typed, setTyped] = useState('')
  const [endAttempts, setEndAttempts] = useState(0)
  const [endRevealed, setEndRevealed] = useState(false)
  const [combo, setCombo] = useState(0)
  const [best, setBest] = useState(0)
  const [showRef, setShowRef] = useState(false)

  const item = items[idx]
  const idxRef = useRef(0)
  const advanceTimerRef = useRef(null)
  const inputRef = useRef(null)
  const cardRef = useRef(null)
  const comboRef = useRef(null)
  const pickRowRef = useRef(null)
  const completedRef = useRef(false)
  useEffect(() => { idxRef.current = idx }, [idx])
  useEffect(() => { if (combo > 0) popScale(comboRef.current) }, [combo])
  useEffect(() => () => clearTimeout(advanceTimerRef.current), [])

  const record = useCallback((i, patch) => {
    setResults(r => {
      const cur = r[i] || { pickAttempts: 0, endAttempts: 0, picks: [], tries: [] }
      return { ...r, [i]: typeof patch === 'function' ? patch(cur) : { ...cur, ...patch } }
    })
  }, [])

  const generate = useCallback(async (isRegen = false) => {
    if (!serverUrl) { setErrorMsg('Mac Studio (REACT_APP_OLLAMA_URI) is not configured.'); setPhase('error'); return }
    if (isRegen) setRegen(true); else setPhase('loading')
    setErrorMsg(''); setLoadingMsg(`Writing ${count} sentences…`)
    try {
      const drafts = (await askModel(serverUrl, apiKey, tensePrompt({ count, query, choices }), 0.5, 2800))
        .map(r => shapeTenseItem(r, allowed)).filter(Boolean)
      if (!drafts.length) throw new Error('The model returned no usable sentences — try again.')
      setLoadingMsg('Checking every answer…')
      let final = drafts
      try {
        const verdicts = await askModel(serverUrl, apiKey, verifyTensePrompt(drafts, choices), 0.1, 2400)
        if (verdicts.length === drafts.length) final = applyTenseVerdicts(drafts, verdicts, allowed)
      } catch { /* the check is best-effort: keep the drafts */ }
      if (!final.length) throw new Error('Every sentence could be read two ways — try again.')
      setItems(final); setIdx(0); setResults({}); setShowSummary(false)
      setSetNum(n => (isRegen ? n + 1 : 1)); setPhase('drill')
    } catch (e) {
      setErrorMsg(failureMessage(e, 'Generation failed.')); setPhase('error')
    }
    setRegen(false); setLoadingMsg('')
  }, [serverUrl, apiKey, count, query, choices, allowed])

  useEffect(() => { generate(false) }, []) // eslint-disable-line

  // Reset per item.
  useEffect(() => {
    if (phase !== 'drill' || !item) return
    setStage('pick'); setChoice(null); setPickResult(null); setPickAttempts(0); setShowHint(false)
    setTyped(''); setEndAttempts(0); setEndRevealed(false)
    completedRef.current = false
  }, [idx, phase, item])
  useEffect(() => { if (stage === 'produce') setTimeout(() => inputRef.current?.focus(), 60) }, [stage])

  // Past the last item → the report card. No side effects inside a state updater.
  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    const next = idxRef.current + 1
    if (next >= items.length) { setShowSummary(true); cueComplete(); return }
    setIdx(next)
  }, [items.length])

  const completeItem = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    setStage('done'); cueCorrect()
    record(idxRef.current, { done: true })
    const el = cardRef.current
    if (el) { const r = el.getBoundingClientRect(); celebrate(r.left + r.width / 2, r.top + r.height / 2, { count: 28, spread: 170 }) }
    setCombo(c => { const nc = c + 1; setBest(b => Math.max(b, nc)); if (nc % 5 === 0) celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 64, spread: 260 }); return nc })
    // Hear the finished sentence — the tense sounds right or it doesn't.
    if (item && !isMuted()) setTimeout(() => speak(item.sentence, voices.eng, voiceSpeed), 380)
    advanceTimerRef.current = setTimeout(advance, 2400)
  }, [advance, record, item, voiceSpeed])

  const pick = (key) => {
    if (!item || stage !== 'pick' || pickResult === 'right' || pickResult === 'revealed') return
    setChoice(key)
    if (key === item.tense) { setPickResult('right'); cueCorrect(); setTimeout(() => setStage('produce'), 700); return }
    const n = pickAttempts + 1
    setPickAttempts(n); setPickResult('wrong'); cueWrong(); shake(pickRowRef.current)
    record(idx, cur => ({ ...cur, pickAttempts: n, picks: cur.picks.includes(key) ? cur.picks : [...cur.picks, key] }))
    if (n >= 2) { setChoice(item.tense); setPickResult('revealed'); record(idx, { revealed: true }) }
  }

  const accepted = useMemo(() => (item ? [item.answer, ...(item.alternatives || [])].map(normEn).filter(Boolean) : []), [item])
  const matches = (val) => !!item && accepted.includes(stripLeadIn(normEn(val), normEn(item.prefix)))
  const onTyped = (val) => { setTyped(val); if (stage === 'produce' && matches(val)) completeItem() }
  const check = () => {
    if (!item || completedRef.current) return
    if (matches(typed)) { completeItem(); return }
    const n = endAttempts + 1
    setEndAttempts(n); cueWrong(); shake(inputRef.current)
    const t = typed.trim()
    record(idx, cur => ({ ...cur, endAttempts: n, tries: t && !cur.tries.includes(t) ? [...cur.tries, t].slice(0, 4) : cur.tries }))
    if (n >= 5) { setEndRevealed(true); record(idx, { revealed: true }) }
  }
  const skip = () => { setCombo(0); record(idx, { skipped: true }); advance() }
  const flagWrong = () => { record(idx, { flagged: true }); advance() }   // a dubious sentence, streak kept
  // Before it's solved, hear the sentence with the gap — never the answer.
  const hear = () => { if (item) speak(stage === 'done' ? item.sentence : `${item.prefix} … ${item.suffix}`, voices.eng, voiceSpeed) }

  const perfectOf = (r) => !!r && !!r.done && !r.revealed && !r.skipped && !r.flagged && !(r.pickAttempts > 0) && !(r.endAttempts > 0)
  const retryTricky = () => {
    const tricky = items.filter((_, i) => !perfectOf(results[i]))
    if (!tricky.length) return
    setItems(tricky); setIdx(0); setResults({}); setShowSummary(false); setCombo(0); setSetNum(n => n + 1)
  }

  if (phase !== 'drill') {
    return (
      <SetLoading label="English · Tenses" phase={phase} loadingMsg={loadingMsg || `Writing ${count} sentences…`} errorMsg={errorMsg}
        themeClass={themeClass} themeBtn={themeBtn} onBack={onBack} onRetry={() => generate(false)} />
    )
  }

  const tense = TENSE_BY_KEY[item?.tense]
  const tenseKnown = pickResult === 'right' || pickResult === 'revealed' || stage !== 'pick'

  return (
    <div className={`${styles.view} ${themeClass}`}>
      {themeBtn}
      <button className={styles.closeBtn} onClick={onBack}><CloseIcon /></button>
      {regen && <div className={styles.regenOverlay}><div className={styles.spinner} /><div className={styles.loadingText}>{loadingMsg || 'New set…'}</div></div>}

      <div className={styles.drill}>
        <div className={styles.drillTop}>
          <span className={styles.modeLabel}>Set {setNum} · Tenses · English</span>
          <div className={styles.comboWrap}><span className={styles.comboFire}>{combo >= 3 ? '🔥' : '✦'}</span><span className={styles.combo} ref={comboRef}>{combo}</span></div>
        </div>

        <div className={styles.card} key={idx} ref={cardRef}>
          <div className={styles.sentenceLine}>
            {item?.prefix && <span className={styles.ctx}>{item.prefix}</span>}
            {stage === 'done'
              ? <span className={styles.answerGot}>{item?.answer}</span>
              : <><span className={styles.cue}>({item?.verb})</span><span className={styles.slot}>{tenseKnown && tense ? tense.short : '···'}</span></>}
            <span className={styles.ctx}>{item?.suffix}</span>
          </div>

          {/* stage 1: which tense does the sentence need? */}
          {stage === 'pick' && (
            <>
              <div className={styles.stageLabel}>Which tense does this sentence need?</div>
              <div className={styles.caseGrid} ref={pickRowRef}>
                {choices.map(c => {
                  const isAnswer = item?.tense === c.key
                  const cls = [
                    styles.caseBtn,
                    isAnswer && (pickResult === 'right' || pickResult === 'revealed') ? styles.caseRight : '',
                    choice === c.key && pickResult === 'wrong' && !isAnswer ? styles.caseWrong : '',
                  ].join(' ')
                  return (
                    <button key={c.key} className={cls} onClick={() => pick(c.key)}>
                      <span className={styles.caseName}>{c.label}</span>
                      <span className={styles.caseQ}>{c.form}</span>
                    </button>
                  )
                })}
              </div>
              <button className={styles.hintBtn} onClick={() => { setShowHint(h => !h); if (!showHint) record(idx, { hinted: true }) }}>
                {showHint ? 'hide hint' : '💡 What should I notice?'}
              </button>
              {showHint && (
                <div className={styles.hintPanel}>
                  Look at <strong>{item?.cue || 'the time words'}</strong>. Is it a habit, something happening now, finished at a finished time, or connected to now?
                </div>
              )}
              {pickResult === 'wrong' && pickAttempts < 2 && <div className={styles.nudge}>Not that one — look at the signal words, then try again.</div>}
              {pickResult === 'revealed' && (
                <>
                  <div className={styles.whyBox}>It’s the <strong>{tense?.label.toLowerCase()}</strong>. {item?.why}</div>
                  <button className={styles.continueBtn} onClick={() => setStage('produce')}>Continue →</button>
                </>
              )}
            </>
          )}

          {/* stage 2: write the verb phrase */}
          {stage === 'produce' && (
            <>
              <div className={styles.whyBox}><strong>{tense?.label}</strong> ✓ — {item?.why}</div>
              <div className={styles.stageLabel}>Now write <strong>{item?.verb}</strong> in the {tense?.label.toLowerCase()}:</div>
              <input ref={inputRef} className={styles.answerInput} value={typed}
                onChange={e => onTyped(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') check() }}
                placeholder="type the verb — with any helper verb…" spellCheck="false" autoComplete="off" autoCapitalize="off" />
              <div className={styles.endActions}>
                <button className={styles.checkBtn} onClick={check}>Check</button>
                {endAttempts >= 1 && !endRevealed && (
                  <span className={styles.endHint}>
                    💡 {tense?.label} looks like “{tense?.form}”{endAttempts >= 2 ? ` · the answer is ${item.answer.split(' ').length} word${item.answer.split(' ').length === 1 ? '' : 's'}` : ''}
                  </span>
                )}
                {!endRevealed && <button className={styles.linkBtn} onClick={() => { setEndRevealed(true); record(idx, { revealed: true }) }}>reveal</button>}
              </div>
              {endRevealed && <div className={styles.revealNote}>Answer: <strong>{item?.answer}</strong> — type it to carry on.</div>}
            </>
          )}

          {stage === 'done' && (
            <>
              <div className={styles.doneMsg}>{pickAttempts === 0 && endAttempts === 0 && !endRevealed ? '★ Perfect — first time' : '✓ Nice'}</div>
              <div className={styles.endActions}>
                <button className={styles.linkBtn} onClick={hear}>🔊 hear the sentence</button>
                <button className={styles.checkBtn} onClick={advance}>Next →</button>
              </div>
            </>
          )}
        </div>

        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={hear} title="Hear the sentence"><VolumeUpIcon /></button>
          <button className={styles.sideBtn} onClick={() => setShowRef(true)} title="When to use each tense"><TableChartIcon /></button>
          <button className={styles.sideBtn} onClick={skip} title="Skip (resets streak)"><SkipNextIcon /></button>
        </div>
        <div className={styles.footRow}>
          <span className={styles.progress}>{idx + 1} / {items.length} · best {best}</span>
          <button className={styles.linkBtn} onClick={flagWrong} title="Skip a dubious sentence — keeps your streak">⚑ looks wrong</button>
        </div>
      </div>

      {showRef && (
        <RuleBoard title="When to use each tense"
          rows={choices.map(c => ({ key: c.key, name: c.label, form: c.form, use: c.use, ex: c.cues }))}
          highlight={tenseKnown ? item?.tense : null}
          onClose={() => setShowRef(false)} />
      )}
      {showSummary && (
        <EndOfSet
          title={`English · Tenses · Set ${setNum}`}
          mode="en-tenses"
          stats={[
            [items.filter((_, i) => (results[i]?.pickAttempts || 0) > 0).length, 'tense slips'],
            [items.filter((_, i) => (results[i]?.endAttempts || 0) > 0).length, 'form slips'],
          ]}
          rows={items.map((it, i) => {
            const r = results[i]
            return { perfect: perfectOf(r), mark: markOf(r), before: it.prefix, answer: it.answer, after: it.suffix, note: `${TENSE_BY_KEY[it.tense]?.label || it.tense} — ${it.why}` }
          })}
          buildDetails={() => items.map((it, i) => {
            const r = results[i] || {}
            return {
              kind: 'verb',
              verb: clip(it.verb, 40), meaning: '',
              tense: TENSE_BY_KEY[it.tense]?.label || it.tense,
              person: '', pronoun: '',
              answer: clip(it.answer, 60),
              sentence: clip(it.sentence, 180),
              // Wrong tense picks read as the tense's name; wrong forms as typed.
              tried: [...(r.picks || []).map(k => TENSE_BY_KEY[k]?.label || k), ...(r.tries || []).map(t => clip(t, 40))].slice(0, 4),
              attempts: (r.pickAttempts || 0) + (r.endAttempts || 0),
              hinted: !!r.hinted, revealed: !!r.revealed, skipped: !!(r.skipped || r.flagged),
              perfect: perfectOf(r),
            }
          })}
          onRetry={retryTricky}
          onNext={() => { setShowSummary(false); generate(true) }}
          onDone={onBack}
        />
      )}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// Articles
// ═════════════════════════════════════════════════════════════════════════════
const ARTICLE_CHOICES = [
  { key: 'a', label: 'a', note: 'one of many · consonant sound' },
  { key: 'an', label: 'an', note: 'one of many · vowel sound' },
  { key: 'the', label: 'the', note: 'you both know which' },
  { key: '—', label: '∅', note: 'no article' },
]
export const EN_ARTICLE_RULES = [
  { key: 'a', name: 'a', use: 'One of many, or mentioned for the first time — before a consonant SOUND.', ex: 'a cat · a university (it starts “you”) · a one-way ticket' },
  { key: 'an', name: 'an', use: 'The same, before a vowel SOUND.', ex: 'an apple · an hour (the h is silent) · an MP3' },
  { key: 'the', name: 'the', use: 'You both know exactly which one: mentioned before, the only one, or made specific.', ex: 'the cat I told you about · the sun · the best day of my life' },
  { key: '—', name: 'no article', use: 'Things in general (plurals and uncountables), most names, meals, and places used for their purpose.', ex: 'Dogs are loyal · I love music · after lunch · at school · by bus' },
]

// "none", "no article", "—", "∅"… all mean the empty choice. An empty string
// does NOT: a missing field is an error, not an answer.
export const normArticle = (a) => {
  const s = String(a == null ? '' : a).trim().toLowerCase()
  if (s === 'a' || s === 'an' || s === 'the') return s
  if (/^(none|no article|no|zero|zero article|null|-|–|—|ø|∅)$/.test(s)) return '—'
  return null
}

const articlePrompt = ({ count, query }) => `You are an experienced English teacher writing an ARTICLES exercise (a / an / the / no article) for a learner whose first language has no articles — Turkish or Russian, for example.
${focusLine(query, 'Mix everyday topics.')}

Create ${count} items. Each is ONE natural everyday sentence with exactly ONE article gap, written ⟦A⟧. Every other article in the sentence must already be written in and correct.
Spread the answers roughly evenly across "a", "an", "the" and "none". Cover the classic cases: first mention versus already known; the only one of something (the sun, the internet); superlatives; a / an decided by SOUND (an hour, a university); things in general with plurals and uncountables (no article); meals, and places used for their purpose (at school, in bed); by + transport.

For each item:
- sentenceTemplate: the complete sentence with the gap written as ⟦A⟧ (e.g. "She is ⟦A⟧ engineer.")
- answer: "a", "an", "the" or "none"
- rule: a 2-5 word label for the rule that decides it (e.g. "first mention", "vowel sound", "only one", "things in general")
- why: ONE short, simple English sentence explaining the choice

Never write a sentence where two answers would both sound natural.
Return ONLY minified JSON, no markdown:
{"items":[{"sentenceTemplate":"","answer":"","rule":"","why":""}]}`

const verifyArticlePrompt = (drafts) => `You are a strict English grammar checker. Each numbered sentence has ONE article gap ⟦A⟧.
For each, decide:
- "answer": the single correct choice — "a", "an", "the" or "none" (no article).
- "ambiguous": true if two choices would both sound natural to a native speaker; otherwise false.
- "rule": a 2-5 word label for the rule that decides it.
- "why": ONE short, simple sentence explaining it.
${drafts.map((d, i) => `${i + 1}. "${d.prefix} ⟦A⟧ ${d.suffix}" — draft: ${d.answer === '—' ? 'none' : d.answer}`).join('\n')}
Return ONLY JSON {"items":[{"answer":"","ambiguous":false,"rule":"","why":""}]} with EXACTLY ${drafts.length} items in the SAME order.`

export const shapeArticleItem = (raw) => {
  const answer = normArticle(raw?.answer)
  const parts = String(raw?.sentenceTemplate || '').trim().split(/⟦[^⟧]*⟧/)
  if (answer == null || parts.length !== 2) return null     // exactly one gap
  const prefix = parts[0].trim()
  const suffix = parts[1].trim()
  if (!suffix) return null                                   // an article needs a noun after it
  return {
    prefix, suffix, answer,
    sentence: capFirst(tidySentence([prefix, answer === '—' ? '' : answer, suffix].filter(Boolean).join(' '))),
    rule: clip(raw?.rule, 40),
    why: String(raw?.why || '').trim(),
  }
}

export const applyArticleVerdicts = (drafts, verdicts) => drafts.map((d, i) => {
  const v = verdicts[i] || {}
  if (v.ambiguous === true || v.ambiguous === 'true') return null
  const answer = normArticle(v.answer)
  if (answer == null) return null
  return shapeArticleItem({
    sentenceTemplate: `${d.prefix} ⟦A⟧ ${d.suffix}`,
    answer: answer === '—' ? 'none' : answer,
    rule: String(v.rule || '').trim() || d.rule,
    why: String(v.why || '').trim() || d.why,
  })
}).filter(Boolean)

const shownArticle = (k) => (k === '—' ? '∅' : k)

export function EnglishArticleView({ count = 10, query = '', voiceSpeed, themeClass = '', themeBtn = null, onBack }) {
  const serverUrl = process.env.REACT_APP_OLLAMA_URI
  const apiKey = process.env.REACT_APP_OLLAMA_KEY

  const [phase, setPhase] = useState('loading')      // loading | drill | error
  const [loadingMsg, setLoadingMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [items, setItems] = useState([])
  const [idx, setIdx] = useState(0)
  const [setNum, setSetNum] = useState(1)
  const [regen, setRegen] = useState(false)
  const [results, setResults] = useState({})
  const [showSummary, setShowSummary] = useState(false)

  const [stage, setStage] = useState('pick')         // pick | done | revealed
  const [choice, setChoice] = useState(null)
  const [attempts, setAttempts] = useState(0)
  const [showHint, setShowHint] = useState(false)
  const [combo, setCombo] = useState(0)
  const [best, setBest] = useState(0)
  const [showRef, setShowRef] = useState(false)

  const item = items[idx]
  const idxRef = useRef(0)
  const advanceTimerRef = useRef(null)
  const cardRef = useRef(null)
  const comboRef = useRef(null)
  const rowRef = useRef(null)
  const completedRef = useRef(false)
  useEffect(() => { idxRef.current = idx }, [idx])
  useEffect(() => { if (combo > 0) popScale(comboRef.current) }, [combo])
  useEffect(() => () => clearTimeout(advanceTimerRef.current), [])

  const record = useCallback((i, patch) => {
    setResults(r => {
      const cur = r[i] || { attempts: 0, picks: [] }
      return { ...r, [i]: typeof patch === 'function' ? patch(cur) : { ...cur, ...patch } }
    })
  }, [])

  const generate = useCallback(async (isRegen = false) => {
    if (!serverUrl) { setErrorMsg('Mac Studio (REACT_APP_OLLAMA_URI) is not configured.'); setPhase('error'); return }
    if (isRegen) setRegen(true); else setPhase('loading')
    setErrorMsg(''); setLoadingMsg(`Writing ${count} sentences…`)
    try {
      const drafts = (await askModel(serverUrl, apiKey, articlePrompt({ count, query }), 0.5, 2200)).map(shapeArticleItem).filter(Boolean)
      if (!drafts.length) throw new Error('The model returned no usable sentences — try again.')
      setLoadingMsg('Checking every answer…')
      let final = drafts
      try {
        const verdicts = await askModel(serverUrl, apiKey, verifyArticlePrompt(drafts), 0.1, 1800)
        if (verdicts.length === drafts.length) final = applyArticleVerdicts(drafts, verdicts)
      } catch { /* the check is best-effort: keep the drafts */ }
      if (!final.length) throw new Error('Every sentence could be read two ways — try again.')
      setItems(final); setIdx(0); setResults({}); setShowSummary(false)
      setSetNum(n => (isRegen ? n + 1 : 1)); setPhase('drill')
    } catch (e) {
      setErrorMsg(failureMessage(e, 'Generation failed.')); setPhase('error')
    }
    setRegen(false); setLoadingMsg('')
  }, [serverUrl, apiKey, count, query])

  useEffect(() => { generate(false) }, []) // eslint-disable-line

  useEffect(() => {
    if (phase !== 'drill' || !item) return
    setStage('pick'); setChoice(null); setAttempts(0); setShowHint(false)
    completedRef.current = false
  }, [idx, phase, item])

  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    const next = idxRef.current + 1
    if (next >= items.length) { setShowSummary(true); cueComplete(); return }
    setIdx(next)
  }, [items.length])

  const complete = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    setStage('done'); cueCorrect()
    record(idxRef.current, { done: true })
    const el = cardRef.current
    if (el) { const r = el.getBoundingClientRect(); celebrate(r.left + r.width / 2, r.top + r.height / 2, { count: 28, spread: 170 }) }
    setCombo(c => { const nc = c + 1; setBest(b => Math.max(b, nc)); if (nc % 5 === 0) celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 64, spread: 260 }); return nc })
    if (item && !isMuted()) setTimeout(() => speak(item.sentence, voices.eng, voiceSpeed), 380)
    advanceTimerRef.current = setTimeout(advance, 2400)
  }, [advance, record, item, voiceSpeed])

  const pick = (key) => {
    if (!item || stage !== 'pick') return
    setChoice(key)
    if (key === item.answer) { complete(); return }
    const n = attempts + 1
    setAttempts(n); cueWrong(); shake(rowRef.current)
    record(idx, cur => ({ ...cur, attempts: n, picks: cur.picks.includes(key) ? cur.picks : [...cur.picks, key] }))
    // Two misses: show the answer and the rule, and wait for Next.
    if (n >= 2) { setStage('revealed'); setCombo(0); record(idx, { revealed: true }) }
  }
  const skip = () => { setCombo(0); record(idx, { skipped: true }); advance() }
  const flagWrong = () => { record(idx, { flagged: true }); advance() }
  const solved = stage === 'done' || stage === 'revealed'
  const hear = () => { if (item) speak(solved ? item.sentence : `${item.prefix} … ${item.suffix}`, voices.eng, voiceSpeed) }

  const perfectOf = (r) => !!r && !!r.done && !r.revealed && !r.skipped && !r.flagged && !(r.attempts > 0)
  const retryTricky = () => {
    const tricky = items.filter((_, i) => !perfectOf(results[i]))
    if (!tricky.length) return
    setItems(tricky); setIdx(0); setResults({}); setShowSummary(false); setCombo(0); setSetNum(n => n + 1)
  }

  if (phase !== 'drill') {
    return (
      <SetLoading label="English · Articles" phase={phase} loadingMsg={loadingMsg || `Writing ${count} sentences…`} errorMsg={errorMsg}
        themeClass={themeClass} themeBtn={themeBtn} onBack={onBack} onRetry={() => generate(false)} />
    )
  }

  // At the start of a sentence the article is capitalised: "The sun…".
  const answerShown = item ? (item.prefix ? shownArticle(item.answer) : capFirst(shownArticle(item.answer))) : ''

  return (
    <div className={`${styles.view} ${themeClass}`}>
      {themeBtn}
      <button className={styles.closeBtn} onClick={onBack}><CloseIcon /></button>
      {regen && <div className={styles.regenOverlay}><div className={styles.spinner} /><div className={styles.loadingText}>{loadingMsg || 'New set…'}</div></div>}

      <div className={styles.drill}>
        <div className={styles.drillTop}>
          <span className={styles.modeLabel}>Set {setNum} · Articles · English</span>
          <div className={styles.comboWrap}><span className={styles.comboFire}>{combo >= 3 ? '🔥' : '✦'}</span><span className={styles.combo} ref={comboRef}>{combo}</span></div>
        </div>

        <div className={styles.card} key={idx} ref={cardRef}>
          <div className={styles.sentenceLine}>
            {item?.prefix && <span className={styles.ctx}>{item.prefix}</span>}
            {solved ? <span className={styles.answerGot}>{answerShown}</span> : <span className={styles.slot}>···</span>}
            <span className={styles.ctx}>{item?.suffix}</span>
          </div>

          {stage === 'pick' && (
            <>
              <div className={styles.stageLabel}>Which goes in the gap?</div>
              <div className={styles.caseGrid} ref={rowRef}>
                {ARTICLE_CHOICES.map(c => (
                  <button key={c.key} className={`${styles.caseBtn} ${choice === c.key && c.key !== item?.answer ? styles.caseWrong : ''}`} onClick={() => pick(c.key)}>
                    <span className={styles.caseName}>{c.label}</span>
                    <span className={styles.caseQ}>{c.note}</span>
                  </button>
                ))}
              </div>
              <button className={styles.hintBtn} onClick={() => { setShowHint(h => !h); if (!showHint) record(idx, { hinted: true }) }}>
                {showHint ? 'hide hint' : '💡 What should I ask myself?'}
              </button>
              {showHint && (
                <div className={styles.hintPanel}>
                  Is it the first time it’s mentioned, or do you both know which one? Is the sentence about things in general? For <strong>a</strong> or <strong>an</strong>, listen to the first <strong>sound</strong> of the next word, not its first letter.
                </div>
              )}
              {attempts === 1 && <div className={styles.nudge}>Not that one — ask yourself the questions in the hint, then try again.</div>}
            </>
          )}

          {solved && (
            <>
              <div className={styles.whyBox}>
                <strong>{item?.answer === '—' ? 'No article' : capFirst(item?.answer || '')}</strong>{item?.rule ? ` · ${item.rule}` : ''} — {item?.why}
              </div>
              {stage === 'done' && <div className={styles.doneMsg}>{attempts === 0 ? '★ Perfect — first time' : '✓ Nice'}</div>}
              <div className={styles.endActions}>
                <button className={styles.linkBtn} onClick={hear}>🔊 hear the sentence</button>
                <button className={styles.checkBtn} onClick={advance}>Next →</button>
              </div>
            </>
          )}
        </div>

        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={hear} title="Hear the sentence"><VolumeUpIcon /></button>
          <button className={styles.sideBtn} onClick={() => setShowRef(true)} title="The article rules"><TableChartIcon /></button>
          <button className={styles.sideBtn} onClick={skip} title="Skip (resets streak)"><SkipNextIcon /></button>
        </div>
        <div className={styles.footRow}>
          <span className={styles.progress}>{idx + 1} / {items.length} · best {best}</span>
          <button className={styles.linkBtn} onClick={flagWrong} title="Skip a dubious sentence — keeps your streak">⚑ looks wrong</button>
        </div>
      </div>

      {showRef && (
        <RuleBoard title="a · an · the · no article" rows={EN_ARTICLE_RULES}
          highlight={solved ? item?.answer : null} onClose={() => setShowRef(false)} />
      )}
      {showSummary && (
        <EndOfSet
          title={`English · Articles · Set ${setNum}`}
          mode="en-articles"
          stats={[
            [items.filter((_, i) => (results[i]?.attempts || 0) > 0).length, 'slips'],
            [items.filter((_, i) => results[i]?.revealed).length, 'revealed'],
          ]}
          rows={items.map((it, i) => {
            const r = results[i]
            return { perfect: perfectOf(r), mark: markOf(r), before: it.prefix, answer: it.prefix ? shownArticle(it.answer) : capFirst(shownArticle(it.answer)), after: it.suffix, note: `${it.rule ? `${it.rule} — ` : ''}${it.why}` }
          })}
          buildDetails={() => items.map((it, i) => {
            const r = results[i] || {}
            return {
              kind: 'choice',
              before: clip(it.prefix, 120), after: clip(it.suffix, 120),
              answer: it.answer, rule: clip(it.rule, 40),
              tried: r.picks || [],
              attempts: r.attempts || 0,
              revealed: !!r.revealed, skipped: !!(r.skipped || r.flagged),
              perfect: perfectOf(r),
            }
          })}
          onRetry={retryTricky}
          onNext={() => { setShowSummary(false); generate(true) }}
          onDone={onBack}
        />
      )}
    </div>
  )
}
